import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tab, Tabs, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { getUserData, updateUser, getPermissions, savePermissions } from '../../services/Users/userService';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import TimeInput from '../Utils/TimeInput';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';
import { removeEncryptedCookie } from '../../utils/encryptedCookie';

const permissionGroups = [
  { name: 'Produto', prefix: 'product', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Empresa', prefix: 'company', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Fazenda', prefix: 'farm', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Colheita', prefix: 'harvest', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Lote', prefix: 'lot', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Silo', prefix: 'silo', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Pessoa', prefix: 'person', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Romaneio', prefix: 'romaneio', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Transferência', prefix: 'transfer', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Desconto', prefix: 'discount', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Usuários', prefix: 'users', fields: ['View', 'Create', 'Update', 'Delete'] },
  { name: 'Configurações', prefix: 'configurations', fields: ['View', 'Update'] },
  { name: 'Gráficos', prefix: 'charts', fields: ['View'] },
  { name: 'Relatórios', prefix: 'reports', fields: ['View'] },
];

const EditUserModal = ({ show, handleClose, user, refreshUsers }) => {
  const { register, handleSubmit, setValue, formState: { errors }, reset, watch } = useForm();
  const { hasPermission } = usePermissions();
  const { loadPermissions } = usePermissionsContext();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [permissions, setPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const canDelete = hasPermission('usersDelete');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user?.username) {
          const data = await getUserData(user.username);
          
          setValue('fullname', data.fullname);
          setValue('role', data.role);
          setValue('active', data.active);
          
          if (data.accessStartTime) {
            setValue('accessStartTime', data.accessStartTime);
          }
          if (data.accessEndTime) {
            setValue('accessEndTime', data.accessEndTime);
          }

          if (user?.id) {
            setLoadingPermissions(true);
            try {
              const permissionsData = await getPermissions(user.id);
              setPermissions(permissionsData || {});
            } catch (error) {
              setPermissions({});
            } finally {
              setLoadingPermissions(false);
            }
          }
        }
      } catch (error) {
        setErrorMessage(error.message || 'Erro ao buscar dados do usuário.');
        setShowErrorModal(true);
      }
    };

    if (show && user) {
      fetchUserData();
    } else if (!show) {
      reset();
      setActiveTab('general');
      setPermissions({});
    }
  }, [show, user, setValue, reset]);

  const accessStartTime = watch('accessStartTime');
  const accessEndTime = watch('accessEndTime');

  const validateTimeRange = (value, fieldName) => {
    if (!value) {
      if (fieldName === 'accessStartTime' && accessEndTime) {
        return 'Início do Acesso é obrigatório quando Final do Acesso está preenchido';
      }
      if (fieldName === 'accessEndTime' && accessStartTime) {
        return 'Final do Acesso é obrigatório quando Início do Acesso está preenchido';
      }
      return true;
    }

    if (!/^\d{2}:\d{2}:\d{2}$/.test(value)) {
      return true;
    }

    if (fieldName === 'accessStartTime' && accessEndTime && /^\d{2}:\d{2}:\d{2}$/.test(accessEndTime)) {
      const start = value.split(':').map(Number);
      const end = accessEndTime.split(':').map(Number);
      const startSeconds = start[0] * 3600 + start[1] * 60 + start[2];
      const endSeconds = end[0] * 3600 + end[1] * 60 + end[2];
      
      if (startSeconds >= endSeconds) {
        return 'Início do Acesso deve ser antes do Final do Acesso';
      }
    }

    if (fieldName === 'accessEndTime' && accessStartTime && /^\d{2}:\d{2}:\d{2}$/.test(accessStartTime)) {
      const start = accessStartTime.split(':').map(Number);
      const end = value.split(':').map(Number);
      const startSeconds = start[0] * 3600 + start[1] * 60 + start[2];
      const endSeconds = end[0] * 3600 + end[1] * 60 + end[2];
      
      if (startSeconds >= endSeconds) {
        return 'Final do Acesso deve ser depois do Início do Acesso';
      }
    }

    return true;
  };

  const onSubmit = async (data) => {
    const payload = {
      fullname: data.fullname,
      role: data.role,
      active: data.active,
    };

    if (data.password) {
      payload.password = data.password;
    }

    if (data.accessStartTime && data.accessEndTime) {
      payload.accessStartTime = data.accessStartTime;
      payload.accessEndTime = data.accessEndTime;
    }

    try {
      await updateUser(user.username, payload);
      setSuccessMessage('Usuário atualizado com sucesso!');
      setShowSuccessModal(true);
      reset();
      setActiveTab('general');
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao atualizar usuário.');
      setShowErrorModal(true);
    }
  };

  const handlePermissionChange = (key, value) => {
    setPermissions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSavePermissions = async () => {
    if (!user?.id) {
      setErrorMessage('ID do usuário não encontrado.');
      setShowErrorModal(true);
      return;
    }

    try {
      await savePermissions(user.id, permissions);
      removeEncryptedCookie('userPermissions');
      await loadPermissions();
      setSuccessMessage('Permissões salvas com sucesso!');
      setShowSuccessModal(true);
    } catch (error) {
      setErrorMessage(error.message || 'Erro ao salvar permissões.');
      setShowErrorModal(true);
    }
  };

  const getPermissionKey = (prefix, field) => {
    const fieldMap = {
      'View': 'View',
      'Create': 'Create',
      'Update': 'Update',
      'Delete': 'Delete'
    };
    return `${prefix}${fieldMap[field]}`;
  };

  const getPermissionValue = (prefix, field) => {
    const key = getPermissionKey(prefix, field);
    return permissions[key] || false;
  };

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshUsers();
    handleClose();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Editar Usuário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            <Tab eventKey="general" title="Dados Gerais">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group controlId="formFullName" className="mb-3">
                  <Form.Label className="mb-1">Nome Completo</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('fullname', { required: 'Nome completo é obrigatório' })}
                    isInvalid={!!errors.fullname}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.fullname && errors.fullname.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="formUsername" className="mb-3">
                  <Form.Label className="mb-1">Usuário</Form.Label>
                  <Form.Control
                    type="text"
                    value={user?.username || ''}
                    readOnly
                  />
                </Form.Group>

                <Form.Group controlId="formPassword" className="mb-3">
                  <Form.Label className="mb-1">Senha (Deixe em branco se não quiser alterar)</Form.Label>
                  <Form.Control
                    type="password"
                    {...register('password')}
                  />
                </Form.Group>

                <Form.Group controlId="formRole" className="mb-3">
                  <Form.Label className="mb-1">Permissão</Form.Label>
                  <Form.Select {...register('role', { required: 'Permissão é obrigatória' })}>
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="formActive" className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Ativo"
                    {...register('active')}
                    disabled={!canDelete}
                  />
                </Form.Group>

                <TimeInput
                  label="Início do Acesso"
                  name="accessStartTime"
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  rules={{
                    validate: (value) => validateTimeRange(value, 'accessStartTime'),
                  }}
                />

                <TimeInput
                  label="Final do Acesso"
                  name="accessEndTime"
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  rules={{
                    validate: (value) => validateTimeRange(value, 'accessEndTime'),
                  }}
                />

                <Button variant="primary" className="button-custom w-100" type="submit">
                  Atualizar
                </Button>
              </Form>
            </Tab>
            <Tab eventKey="permissions" title="Permissões">
              {loadingPermissions ? (
                <div className="text-center p-4">Carregando permissões...</div>
              ) : (
                <div>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th style={{ width: '20%' }}>Módulo</th>
                        <th style={{ width: '20%', textAlign: 'center' }}>Visualizar</th>
                        <th style={{ width: '20%', textAlign: 'center' }}>Cadastrar</th>
                        <th style={{ width: '20%', textAlign: 'center' }}>Editar</th>
                        <th style={{ width: '20%', textAlign: 'center' }}>Remover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissionGroups.map((group) => (
                        <tr key={group.prefix}>
                          <td><strong>{group.name}</strong></td>
                          <td style={{ textAlign: 'center' }}>
                            {group.fields.includes('View') ? (
                              <Form.Check
                                type="checkbox"
                                checked={getPermissionValue(group.prefix, 'View')}
                                onChange={(e) => handlePermissionChange(getPermissionKey(group.prefix, 'View'), e.target.checked)}
                              />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {group.fields.includes('Create') ? (
                              <Form.Check
                                type="checkbox"
                                checked={getPermissionValue(group.prefix, 'Create')}
                                onChange={(e) => handlePermissionChange(getPermissionKey(group.prefix, 'Create'), e.target.checked)}
                              />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {group.fields.includes('Update') ? (
                              <Form.Check
                                type="checkbox"
                                checked={getPermissionValue(group.prefix, 'Update')}
                                onChange={(e) => handlePermissionChange(getPermissionKey(group.prefix, 'Update'), e.target.checked)}
                              />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {group.fields.includes('Delete') ? (
                              <Form.Check
                                type="checkbox"
                                checked={getPermissionValue(group.prefix, 'Delete')}
                                onChange={(e) => handlePermissionChange(getPermissionKey(group.prefix, 'Delete'), e.target.checked)}
                              />
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button 
                    variant="primary" 
                    className="button-custom w-100 mt-3" 
                    onClick={handleSavePermissions}
                  >
                    Salvar Permissões
                  </Button>
                </div>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditUserModal;